import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';

export type ResponsiveBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const BREAKPOINTS: ResponsiveBreakpoint[] = ['sm', 'md', 'lg', 'xl', '2xl'];

/**
 * Semantic breakpoint hooks for behavior-level responsive decisions.
 *
 * Best practice in this codebase:
 * - Prefer NativeWind/Gluestack responsive classes for styling and layout.
 * - Use these hooks only when viewport width changes behavior, such as
 *   switching component trees, navigation flows, or interaction patterns.
 * - Keep breakpoint translation centralized here instead of re-encoding
 *   `{ default, md, lg, xl, 2xl }` maps in feature code.
 *
 * Important: the underlying breakpoint resolver does not treat a missing
 * larger breakpoint as "keep the current boolean". For example,
 * `{ default: true, md: false }` can resolve back to `true` at `lg/xl/2xl`.
 * These helpers therefore expand the requested breakpoint across all larger
 * breakpoints so they preserve NativeWind's min-width semantics.
 */
function buildBelowBreakpointValues(
  breakpoint: ResponsiveBreakpoint
): Record<'default' | ResponsiveBreakpoint, boolean> {
  const values = { default: true } as Record<'default' | ResponsiveBreakpoint, boolean>;

  const startIndex = BREAKPOINTS.indexOf(breakpoint);

  for (let index = startIndex; index < BREAKPOINTS.length; index += 1) {
    values[BREAKPOINTS[index]] = false;
  }

  return values;
}

function buildAtOrAboveBreakpointValues(
  breakpoint: ResponsiveBreakpoint
): Record<'default' | ResponsiveBreakpoint, boolean> {
  const values = { default: false } as Record<'default' | ResponsiveBreakpoint, boolean>;

  const startIndex = BREAKPOINTS.indexOf(breakpoint);

  for (let index = startIndex; index < BREAKPOINTS.length; index += 1) {
    values[BREAKPOINTS[index]] = true;
  }

  return values;
}

/**
 * True when the viewport is below the provided Tailwind breakpoint.
 * Mirrors mobile-first semantics: `md:` means `md` and up, so "below md"
 * is the compact/mobile layout case.
 */
export function useIsBelowBreakpoint(breakpoint: ResponsiveBreakpoint) {
  return useBreakpointValue(buildBelowBreakpointValues(breakpoint)) ?? true;
}

/**
 * True when the viewport is at or above the provided Tailwind breakpoint.
 * Mirrors Tailwind/NativeWind min-width breakpoint behavior.
 */
export function useIsAtOrAboveBreakpoint(breakpoint: ResponsiveBreakpoint) {
  return useBreakpointValue(buildAtOrAboveBreakpointValues(breakpoint)) ?? false;
}

/**
 * Layout-focused alias for the app's primary compact vs expanded split.
 * Compact layout corresponds to viewport widths below `md`.
 */
export function useIsCompactLayout() {
  return useIsBelowBreakpoint('md');
}
